import {
  AnythingCategory,
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
} from 'constants/experience'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { unique, without } from 'lib/array/arrayUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import {
  getOptimizelyFeatureFlagVariation,
  isAllowedToTriggerExperiment,
} from 'lib/optimizely/optimizelyUtils'
import type { ExperienceCoordinates } from 'lib/experiences/experienceUtils'

interface ExperienceSearchStreamingFilters {
  placeId?: string
  from?: string
  to?: string
  priceLte?: number
  priceGte?: number
  minRating?: number
  isGift?: boolean
  targetAudience?: Array<App.ExperienceTargetAudience>
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  bounds?: string
  leExclusive?: boolean
  coordinatesList?: Array<ExperienceCoordinates>
  timeSlots?: Array<Experiences.TimeSlotFilter>
  /** Carousels need limit > 0. Count/existence probes use countOnly instead. */
  limit?: number
  countOnly?: boolean
}

function isExperienceSearchStreamingFlagOn(state: App.State): boolean {
  return isAllowedToTriggerExperiment(
    true,
    state.utm.content,
    OptimizelyFeatureFlags.experiencesSearchStreaming,
  ) && getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.experiencesSearchStreaming)
}

export function isExperienceSearchPagePath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, { path: '/:regionCode/search/experiences', exact: true })
}

export function isExperienceVerticalPagePath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, { path: '/:regionCode/experiences', exact: true })
}

function hasUnsupportedHomepageStreamFilter(filters: ExperienceSearchStreamingFilters): boolean {
  // Pin / nearby coordinate lists are list-enriched only. Vision uses bounds and lat/lng.
  return !!filters.coordinatesList?.length
}

export function shouldUseExperienceSearchStreaming(
  state: App.State,
  filters: ExperienceSearchStreamingFilters,
): boolean {
  if (!filters.placeId || !isExperienceSearchStreamingFlagOn(state)) return false

  const pathname = state.router.location.pathname
  if (isExperienceSearchPagePath(pathname)) return true

  const hasCarouselLimit = filters.limit != null && filters.limit > 0
  if (!hasCarouselLimit && !filters.countOnly) return false
  if (hasUnsupportedHomepageStreamFilter(filters)) return false
  if (!isExperienceVerticalPagePath(pathname)) return false

  return true
}

export function mapSearchStreamingCategories(
  categoryCodes: Array<number>,
  categories: Array<App.ExperienceItemCategory>,
): Array<string> | undefined {
  const codes = without(
    categoryCodes,
    AnythingCategory.id,
    EXP_CATEGORY_LUX_PLUS_ID,
    EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  )
  if (!codes.length) return []

  const categoryById = new Map(categories.map(category => [Number(category.id), category]))
  const slugs: Array<string> = []

  for (const categoryId of codes) {
    const category = categoryById.get(Number(categoryId))
    if (!category?.name) return
    const slug = experienceCategoryVisionSlug(category.name)

    if (category.level === 'child' && category.parentId) {
      const parentName = categoryById.get(Number(category.parentId))?.name
      const parentSlug = parentName ? experienceCategoryVisionSlug(parentName) : undefined
      if (!parentSlug) return
      slugs.push(`${parentSlug}:${slug}`)
      continue
    }

    slugs.push(slug)
  }

  return unique(slugs)
}

/** Vision `features` CSV slugs from list-enriched boolean flags. */
export function mapSearchStreamingFeatures(filters: {
  leExclusive?: boolean
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  isGift?: boolean
}): Array<string> | undefined {
  const features: Array<string> = []
  if (filters.leExclusive) features.push('le_exclusive')
  if (filters.hasFreeCancellation) features.push('free_cancellation')
  if (filters.hasInstantConfirmation) features.push('instant_confirmation')
  if (filters.isGift) features.push('gifting_available')
  return features.length ? features : undefined
}

// Match svc-search `slugify` used when indexing experience category tags.
function experienceCategoryVisionSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}
